import { Trans, useLingui } from "@lingui/react/macro";
import type { Routine } from "@rakazo/contracts";
import { Button } from "@rakazo/ui-web";
import { useEffect, useState } from "react";
import { useSpaceRpc } from "../../lib/space-rpc";
import { routineListTitle } from "../RoutineEditor";
import { subscribeRoutineChanges } from "./routine-live";

/** Routines for this bot, with pause and resume, on the settings panel. */
export function BotRoutinesSection({ botId }: { botId: string }) {
  const rpc = useSpaceRpc();
  const { t } = useLingui();
  const [routines, setRoutines] = useState<Routine[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRoutines(null);
    const list = rpc.routines?.list;
    if (!list) {
      setRoutines([]);
      return;
    }
    void list({ botId })
      .then((rows) => {
        if (!cancelled) setRoutines(rows);
      })
      .catch(() => {
        if (!cancelled) setRoutines([]);
      });
    return () => {
      cancelled = true;
    };
  }, [botId, rpc]);

  useEffect(() => {
    return subscribeRoutineChanges((id) => {
      if (id !== botId) return;
      const list = rpc.routines?.list;
      if (!list) return;
      void list({ botId })
        .then(setRoutines)
        .catch(() => undefined);
    });
  }, [botId, rpc]);

  async function toggle(routine: Routine) {
    const updated = await rpc.routines.update({
      routineId: routine.id,
      active: !routine.active,
    });
    setRoutines(
      (current) => current?.map((item) => (item.id === routine.id ? updated : item)) ?? current,
    );
  }

  return (
    <section className="mt-6" data-testid="bot-routines">
      <h3 className="mb-2 text-xs font-medium text-muted-foreground">
        <Trans>Routines</Trans>
      </h3>
      {routines && routines.length > 0 ? (
        <ul className="space-y-2">
          {routines.map((routine) => (
            <li
              key={routine.id}
              className="flex items-center justify-between gap-3 rounded-xl bg-muted/50 px-4 py-3"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm text-foreground">
                  {routineListTitle(routine)}
                </span>
                <span className="text-[12px] text-muted-foreground" data-testid="routine-status">
                  {routine.active ? <Trans>Active</Trans> : <Trans>Paused</Trans>}
                </span>
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-label={
                  routine.active
                    ? t`Pause ${routineListTitle(routine)}`
                    : t`Resume ${routineListTitle(routine)}`
                }
                onClick={() => void toggle(routine)}
              >
                {routine.active ? <Trans>Pause</Trans> : <Trans>Resume</Trans>}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
